// frontend/src/components/MikrotikConfigCard.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import "./MikrotikConfigCard.css";
import {
  Boton, EncabezadoPagina, Iconos, Insignia,
} from "../componentes/base";

/**
 * Axios configurado para hablar con Flask.
 * Adjunta el token JWT en cada petición (el backend exige autenticación)
 * y, si el token venció, limpia la sesión y regresa al login.
 */
const api = axios.create({
  baseURL: "/api",
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (error) => {
    if (
      error?.response?.status === 401 &&
      !window.location.pathname.startsWith("/login")
    ) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

const MikrotikConfigCard = () => {
  const [routers, setRouters] = useState([]);
  const [form, setForm] = useState({
    id: null,
    nombre: "",
    ip: "",
    puerto: 8728,
    usuario: "",
    password: "",
    descripcion: "",
  });
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);

  // ---------------------------------------------------
  // CARGAR ROUTERS
  // ---------------------------------------------------
  const loadRouters = async () => {
    try {
      const res = await api.get("/mikrotik/list");
      setRouters(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Error cargando routers:", err.response || err);
      setRouters([]);
    }
  };

  useEffect(() => {
    loadRouters();
  }, []);

  // ---------------------------------------------------
  // FORM
  // ---------------------------------------------------
  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // ---------------------------------------------------
  // TEST CONEXIÓN
  // ---------------------------------------------------
  const handleTest = async () => {
    setTesting(true);
    setStatus(null);
    try {
      const res = await api.post("/mikrotik/test", {
        ip: form.ip,
        usuario: form.usuario,
        password: form.password,
        puerto: Number(form.puerto) || 8728,
      });
      setStatus({
        type: res.data.ok ? "success" : "error",
        msg: res.data.mensaje,
      });
    } catch (err) {
      setStatus({
        type: "error",
        msg:
          err.response?.data?.mensaje ||
          "Error probando conexión con Mikrotik.",
      });
    } finally {
      setTesting(false);
    }
  };

  // ---------------------------------------------------
  // GUARDAR
  // ---------------------------------------------------
  const handleSave = async () => {
    setSaving(true);
    setStatus(null);
    try {
      const res = await api.post("/mikrotik/save", {
        ...form,
        puerto: Number(form.puerto) || 8728,
      });

      setStatus({
        type: res.data.ok ? "success" : "error",
        msg: res.data.mensaje,
      });

      if (res.data.ok) {
        setForm((prev) => ({
          ...prev,
          id: res.data.id || prev.id,
          password: "",
        }));
        loadRouters();
      }
    } catch (err) {
      setStatus({
        type: "error",
        msg:
          err.response?.data?.mensaje ||
          "Error guardando conexión Mikrotik.",
      });
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------
  // SELECCIONAR ROUTER
  // ---------------------------------------------------
  const handleSelectRouter = (router) => {
    setForm({
      id: router.id,
      nombre: router.nombre,
      ip: router.ip,
      puerto: router.puerto,
      usuario: router.usuario,
      password: "",
      descripcion: router.descripcion || "",
    });
    setStatus(null);
  };

  const cleanForm = () => {
    setForm({
      id: null,
      nombre: "",
      ip: "",
      puerto: 8728,
      usuario: "",
      password: "",
      descripcion: "",
    });
    setStatus(null);
  };

  // ---------------------------------------------------
  // UI
  // ---------------------------------------------------
  return (
    <div className="mikrotik-page">
      <EncabezadoPagina
        icono={Iconos.equipos}
        titulo="Conexión MikroTik"
        resumen="API 8728 · configura y guarda la conexión para administrar el router."
        acciones={
          <Insignia
            variante={form.id ? "info" : "neutra"}
            icono={form.id ? Iconos.editar : Iconos.agregar}
            texto={form.id ? "Editando conexión" : "Nueva conexión"}
          />
        }
      />

      <div className="mikrotik-card">

        <div className="mikrotik-grid">
          <div className="field">
            <label>Nombre</label>
            <input name="nombre" value={form.nombre} onChange={onChange} />
          </div>

          <div className="field">
            <label>IP / Host</label>
            <input name="ip" value={form.ip} onChange={onChange} />
          </div>

          <div className="field">
            <label>Puerto API</label>
            <input
              name="puerto"
              type="number"
              value={form.puerto}
              onChange={onChange}
            />
          </div>

          <div className="field">
            <label>Usuario</label>
            <input name="usuario" value={form.usuario} onChange={onChange} />
          </div>

          <div className="field">
            <label>Contraseña</label>
            <input
              name="password"
              type="password"
              value={form.password}
              onChange={onChange}
            />
          </div>

          <div className="field field-full">
            <label>Descripción</label>
            <textarea
              name="descripcion"
              value={form.descripcion}
              onChange={onChange}
              rows={2}
            />
          </div>
        </div>

        {status && (
          <div
            className={`mikrotik-alert ${
              status.type === "success" ? "ok" : "error"
            }`}
          >
            {status.msg}
          </div>
        )}

        <div className="mikrotik-actions">
          <Boton variante="secundario" icono={Iconos.cerrar} onClick={cleanForm}>
            Limpiar
          </Boton>
          <div className="mikrotik-actions-right">
            <Boton
              variante="secundario"
              icono={Iconos.senal}
              cargando={testing}
              disabled={saving}
              onClick={handleTest}
            >
              Probar conexión
            </Boton>
            <Boton
              icono={Iconos.confirmar}
              cargando={saving}
              onClick={handleSave}
            >
              Guardar conexión
            </Boton>
          </div>
        </div>
      </div>

      <div className="mikrotik-table-card">
        <div className="mikrotik-table-header">
          <h3>
            <Iconos.equipos size={16} strokeWidth={1.8} />
            MikroTik guardados
          </h3>
          <Insignia variante="neutra" texto={`${routers.length} registrados`} />
        </div>

        <table className="mikrotik-table">
          <thead>
            <tr>
              <th>Nombre</th>
              <th>IP</th>
              <th>Puerto</th>
              <th>Usuario</th>
              <th>Estado</th>
              <th>Última conexión OK</th>
              <th>Detalle</th>
            </tr>
          </thead>
          <tbody>
            {routers.length === 0 && (
              <tr>
                <td colSpan="7" className="no-data">
                  No hay conexiones guardadas.
                </td>
              </tr>
            )}

            {routers.map((r) => (
              <tr
                key={r.id}
                onClick={() => handleSelectRouter(r)}
                className="row-clickable"
              >
                <td>{r.nombre}</td>
                <td>{r.ip}</td>
                <td>{r.puerto}</td>
                <td>{r.usuario}</td>
                <td>
                  {r.ultimo_estado === "online" ? (
                    <Insignia variante="activo" icono={Iconos.enLinea} texto="En línea" />
                  ) : r.ultimo_estado === "offline" ? (
                    <Insignia variante="cortado" icono={Iconos.fueraDeLinea} texto="Fuera de línea" />
                  ) : (
                    /* Nunca se ha probado: distinto de estar caído. */
                    <Insignia variante="neutra" icono={Iconos.duda} texto="Sin probar" />
                  )}
                </td>
                <td>
                  {r.ultima_conexion_ok
                    ? new Date(r.ultima_conexion_ok).toLocaleString()
                    : "-"}
                </td>
                <td className="mensaje-cell">
                  {r.ultimo_mensaje || "-"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MikrotikConfigCard;

